/* eslint-disable no-undef */
// @flow
import { useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import '@procot/webostv/webOSTV'

import {
  prepareCurrentNetworkStatus,
  CONNECTED,
} from '@alphaott/smart-tv-common/src/hooks/useNetworkStatus'
import { setDeviceInfo } from '@alphaott/smart-tv-current-device/actions'

export const useInitDeviceInfo = () => {
  const dispatch = useDispatch()

  const setDeviceSystemInfo = useCallback(
    (response) => {
      const isSuccess = R.prop('returnValue', response)
      const model = R.prop('modelName', response)
      const osVersion = R.prop('sdkVersion', response)
      if (isSuccess) {
        dispatch(
          setDeviceInfo({
            brand: 'LG',
            model,
            os: 'WebOS',
            osVersion,
            visibilityStatus: document.hidden ? 'hidden' : 'visible',
          }),
        )
      }
    },
    [dispatch],
  )

  const setDeviceId = useCallback(
    (response) => {
      const isSuccess = R.prop('returnValue', response)
      const idList = R.propOr([], 'idList', response)
      const id = R.prop('idValue', R.head(idList))
      if (isSuccess) {
        dispatch(
          setDeviceInfo({
            _id: id,
            serial: id,
          }),
        )
      }
    },
    [dispatch],
  )

  const setNetworkInfo = useCallback(
    // eslint-disable-next-line complexity
    (response) => {
      const wifiState = R.path(['wifi', 'state'], response)
      const wiredState = R.path(['wired', 'state'], response)
      const isConnectedWifi = R.equals(wifiState, CONNECTED)
      const isConnectedWired = R.equals(wiredState, CONNECTED)
      const networkStatus = prepareCurrentNetworkStatus(R.or(isConnectedWifi, isConnectedWired))

      const isSuccess = R.prop('returnValue', response)
      if (isSuccess) {
        dispatch(
          setDeviceInfo({
            networkStatus,
            network: {
              ...(wifiState === 'connected' && {
                mode: 'WIFI',
                localIpAddress: response?.wifi?.ipAddress,
              }),

              ...(wiredState === 'connected' && {
                mode: 'ETHERNET',
                localIpAddress: response?.wired?.ipAddress,
              }),
            },
          }),
        )
      }
    },
    [dispatch],
  )

  const setVisibilityStatus = useCallback(() => {
    const currentVisibility = !document.hidden

    requestAnimationFrame(() => {
      const nextFrameVisibility = !document.hidden
      const isVisible = nextFrameVisibility ?? currentVisibility

      dispatch(
        setDeviceInfo({
          visibilityStatus: isVisible ? 'visible' : 'hidden',
        }),
      )
    })
  }, [dispatch])

  useEffect(() => {
    webOS.service.request('luna://com.webos.service.tv.systemproperty', {
      method: 'getSystemInfo',
      parameters: {
        keys: ['modelName', 'firmwareVersion', 'sdkVersion'],
      },
      onComplete: setDeviceSystemInfo,
    })
    webOS.service.request('luna://com.webos.service.sm', {
      method: 'deviceid/getIDs',
      parameters: {
        idType: ['LGUDID'],
      },
      onSuccess: setDeviceId,
    })
    webOS.service.request('luna://com.webos.service.connectionmanager', {
      method: 'getStatus',
      parameters: {
        subscribe: true,
      },
      onSuccess: setNetworkInfo,
    })
    document.addEventListener('visibilitychange', setVisibilityStatus)

    return () => {
      document.removeEventListener('visibilitychange', setVisibilityStatus)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}
